import styled from '@emotion/styled/macro';
import vars from '@variables';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchBox } from 'react-instantsearch';
import { useCustomSearch } from './CustomSearchContext';
import Translate from '../../utils/translation-functions';
import { orderQueryString } from '../../../../utils/string-helper-functions';

// ----------------------
// Locale templating utils (same as page)
// ----------------------
function formatTpl(tpl, vars = {}) {
  return tpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? '');
}
function buildCopyFromTemplates(templates, locale = 'en-GB', key, vars = {}) {
  const dict = templates?.[locale] || templates?.['en-GB'] || {};
  const fallback = templates?.['en-GB'] || {};
  const tpl = dict[key] || fallback[key] || '';
  return formatTpl(tpl, vars);
}

// Allowed topic keys (as they appear in the URL) → human labels
const TOPIC_LABELS = {
  Biodiversity: 'Biodiversity',
  Business: 'Business',
  CircularEconomyExplained: 'explained', // special-case: short label to avoid duplicate "circular economy"
  BuiltEnvironment: 'Built environment',
  Cities: 'Cities',
  Climate: 'Climate',
  CriticalMinerals: 'Critical minerals',
  Design: 'Design',
  Education: 'Education',
  Fashion: 'Fashion',
  Finance: 'Finance',
  Food: 'Food',
  Mission: 'Mission',
  Network: 'Network',
  Plastics: 'Plastics',
  Policy: 'Policy',
  Retail: 'Retail',
};

const ALLOWED_CONTENT_TYPE_KEYS = new Set([
  'Article',
  'CaseStudy',
  'Collection',
  'Event',
  'ExternalResource',
  'LearningResource',
  'News',
  'Podcast',
  'PressRelease',
  'Project',
  'Publication',
  'Toolkit',
  'Video',
  'WorkshopResource',
]);

// Optional English fallbacks if a localized label is missing:
const CONTENT_TYPE_LABELS_FALLBACK = {
  Article: 'Article',
  CaseStudy: 'Case study',
  Collection: 'Collection',
  Event: 'Event',
  ExternalResource: 'External resource',
  LearningResource: 'Learning resource',
  News: 'News',
  Podcast: 'Podcast',
  PressRelease: 'Press release',
  Project: 'Project',
  Publication: 'Publication',
  Toolkit: 'Toolkit',
  Video: 'Video',
  WorkshopResource: 'Workshop resource',
};

const CONTENT_TYPE_PLURALS_EN_FALLBACK = {
  Article: 'articles',
  CaseStudy: 'case studies',
  Collection: 'collections',
  Event: 'events',
  ExternalResource: 'external resources',
  LearningResource: 'learning resources',
  News: 'news',
  Podcast: 'podcasts',
  PressRelease: 'press releases',
  Project: 'projects',
  Publication: 'publications',
  Toolkit: 'toolkits',
  Video: 'videos',
  WorkshopResource: 'workshop resources',
};

const ALLOWED_TOPIC_KEYS = new Set(Object.keys(TOPIC_LABELS));

function sentenceCase(str = '', locale = undefined) {
  const s = String(str).trim();
  if (!s) return '';
  return (
    s.charAt(0).toLocaleUpperCase(locale) + s.slice(1).toLocaleLowerCase(locale)
  );
}

const SearchBox = props => {
  const { updateSearchValue } = useCustomSearch();
  const { query, refine } = useSearchBox(props);

  const inputRef = useRef(null);
  const [dynamicTitle, setDynamicTitle] = useState(props.title);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      emitGAEvent(query);
    }, 750);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined')
      return;

    const setRobots = content => {
      let m = document.querySelector('meta[name="robots"]');
      if (!m) {
        m = document.createElement('meta');
        m.setAttribute('name', 'robots');
        document.head.appendChild(m);
      }
      m.setAttribute('content', content);
    };

    const setCanonical = href => {
      let l = document.querySelector('link[rel="canonical"]');
      if (!l) {
        l = document.createElement('link');
        l.setAttribute('rel', 'canonical');
        document.head.appendChild(l);
      }
      l.setAttribute('href', href);
    };

    const setOgUrl = href => {
      let l = document.querySelector('meta[property="og:url"]');
      if (!l) {
        l = document.createElement('meta');
        l.setAttribute('property', 'og:url');
        document.head.appendChild(l);
      }
      l.setAttribute('content', href);
    };

    const setMetaDescription = content => {
      let m = document.querySelector('meta[name="description"]');
      if (!m) {
        m = document.createElement('meta');
        m.setAttribute('name', 'description');
        document.head.appendChild(m);
      }
      m.setAttribute('content', content);
    };

    const setIntroText = text => {
      const el = document.getElementById('content-hub-intro');
      if (el) el.textContent = text || '';
    };

    const updateHead = (
      title,
      robots,
      canonicalHref,
      metaDescription,
      introText
    ) => {
      document.title = title;
      setRobots(robots);
      setCanonical(canonicalHref);
      setOgUrl(canonicalHref);
      if (metaDescription !== undefined) setMetaDescription(metaDescription);
      if (introText !== undefined) setIntroText(introText);
    };

    const computeFromUrl = () => {
      const { pathname, search } = window.location;
      const params = new URLSearchParams(search);
      const locale = props.locale || 'en-GB';
      const templates = props.templates;

      const topicsParam = params.get('topics');
      const topicKeys = topicsParam
        ? topicsParam.split('_').filter(Boolean)
        : [];

      const ctParam = params.get('contentType');
      const ctKeys = ctParam ? ctParam.split('_').filter(Boolean) : [];

      const hasOther =
        params.has('geography') || params.has('format') || params.has('search');

      // Base /explore (no filters at all)
      if (!topicsParam && !ctParam && !hasOther) {
        const baseTitle = props.baseTitle || props.title;
        const baseMetaTitle = props.baseMetaTitle || baseTitle;
        const baseMetaDesc = props.baseMetaDescription || '';
        const displayH1 = sentenceCase(baseTitle, locale);
        const displayMeta = sentenceCase(baseMetaTitle, locale);
        setDynamicTitle(displayH1);
        updateHead(
          displayMeta,
          'index,follow',
          `${vars.baseDomain}${pathname}`,
          baseMetaDesc,
          props.introTextSSR || ''
        );
        return;
      }

      const singleTopic =
        topicKeys.length === 1 && ALLOWED_TOPIC_KEYS.has(topicKeys[0]);
      const zeroOrOneCt =
        ctKeys.length <= 1 &&
        (ctKeys.length === 0 || ALLOWED_CONTENT_TYPE_KEYS.has(ctKeys[0]));

      if (singleTopic && zeroOrOneCt && !hasOther) {
        const isCEE = topicKeys[0] === 'CircularEconomyExplained';
        const hasSingleCt = ctKeys.length === 1;
        const isCol = hasSingleCt && ctKeys[0] === 'Collection';

        // Topic localized (use provided map if present)
        const topicLabelRaw =
          (props.topicLabelMap && props.topicLabelMap[topicKeys[0]]) ||
          TOPIC_LABELS[topicKeys[0]] ||
          topicKeys[0];

        // For readable phrases, use "Circular economy explained" when CEE on EN;
        // for es/pt we rely on the dedicated templates (h1_cee / h1_cee_ct_*), so no duplication.
        const ceWord = 'Circular economy';
        const topicReadable = isCEE
          ? `${ceWord} ${TOPIC_LABELS['CircularEconomyExplained']}`
          : topicLabelRaw;

        const ctKey = hasSingleCt ? ctKeys[0] : null;
        const ctLabelSingular = hasSingleCt
          ? (props.contentTypeLabelMap && props.contentTypeLabelMap[ctKey]) ||
            CONTENT_TYPE_LABELS_FALLBACK[ctKey] ||
            ctKey
          : '';
        const ctLabelPlural = hasSingleCt
          ? (props.contentTypePluralLabelMap &&
              props.contentTypePluralLabelMap[ctKey]) ||
            CONTENT_TYPE_PLURALS_EN_FALLBACK[ctKey] ||
            ctLabelSingular
          : '';

        // Choose gender for es/pt when 1 content type
        const ctGender =
          hasSingleCt && (locale === 'es' || locale === 'pt')
            ? props.contentTypeGenderMap?.[locale]?.[ctKey] || 'masc'
            : null;

        // H1 via templates
        let h1;
        if (hasSingleCt) {
          const key =
            locale === 'es' || locale === 'pt'
              ? isCEE
                ? `h1_cee_ct_${ctGender}`
                : `h1_topic_ct_${ctGender}`
              : isCEE
                ? 'h1_cee_ct'
                : 'h1_topic_ct';
          h1 = buildCopyFromTemplates(templates, locale, key, {
            topic: topicReadable,
            ctypePlural: ctLabelPlural,
          });
        } else {
          const key = isCEE ? 'h1_cee' : 'h1_topic';
          h1 = buildCopyFromTemplates(templates, locale, key, {
            topic: topicReadable,
          });
        }
        h1 = sentenceCase(h1, locale);
        setDynamicTitle(h1);

        // Meta title (brand outside sentenceCase)
        const brand = props.brandName || 'Ellen MacArthur Foundation';
        let left;
        if (hasSingleCt) {
          left = isCEE
            ? buildCopyFromTemplates(templates, locale, 'title_cee_ct', {
                ctypePlural: ctLabelPlural,
              })
            : buildCopyFromTemplates(templates, locale, 'title_topic_ct', {
                topic: topicReadable,
                ctypePlural: ctLabelPlural,
              });
        } else {
          left = isCEE
            ? buildCopyFromTemplates(templates, locale, 'title_cee')
            : buildCopyFromTemplates(templates, locale, 'title_topic', {
                topic: topicReadable,
              });
        }
        const metaTitle = `${sentenceCase(left, locale)} - ${brand}`;

        // Meta description
        let metaDesc;
        if (hasSingleCt) {
          metaDesc = isCEE
            ? buildCopyFromTemplates(templates, locale, 'desc_cee_ct', {
                ctypePlural: ctLabelPlural,
              })
            : buildCopyFromTemplates(templates, locale, 'desc_topic_ct', {
                topic: topicReadable.toLowerCase(),
                ctypePlural: ctLabelPlural,
              });
        } else {
          metaDesc = isCEE
            ? buildCopyFromTemplates(templates, locale, 'desc_cee')
            : buildCopyFromTemplates(templates, locale, 'desc_topic', {
                topic: topicReadable.toLowerCase(),
              });
        }

        // Intro
        let intro;
        if (hasSingleCt) {
          const introKey = isCEE
            ? 'intro_cee_ct'
            : isCol
              ? 'intro_topic_ct_col'
              : 'intro_topic_ct';
          intro = buildCopyFromTemplates(templates, locale, introKey, {
            topic: topicReadable.toLowerCase(),
            ctypePlural: ctLabelPlural,
          });
        } else {
          const introKey = isCEE ? 'intro_cee' : 'intro_topic';
          intro = buildCopyFromTemplates(templates, locale, introKey, {
            topic: topicReadable.toLowerCase(),
          });
        }

        const orderedQs = orderQueryString(search);

        updateHead(
          metaTitle,
          'index,follow',
          `${vars.baseDomain}${pathname}${orderedQs}`,
          metaDesc,
          intro
        );
        return;
      }

      // Everything else => noindex + canonical to base
      const baseTitle = props.baseTitle || props.title;
      const baseMetaTitle = props.baseMetaTitle || baseTitle;
      const baseMetaDesc = props.baseMetaDescription || '';
      const displayH1 = sentenceCase(baseTitle, locale);
      const displayMeta = sentenceCase(baseMetaTitle, locale);
      setDynamicTitle(displayH1);
      updateHead(
        displayMeta,
        'noindex,follow',
        `${vars.baseDomain}${pathname}`,
        baseMetaDesc,
        props.introTextSSR || ''
      );
    };

    // Run now (initial render / direct visits)
    computeFromUrl();

    // Listen for SPA URL changes (Algolia uses history APIs)
    const handle = () => computeFromUrl();
    window.addEventListener('popstate', handle);
    window.addEventListener('locationchange', handle);

    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    const notify = () => window.dispatchEvent(new Event('locationchange'));
    window.history.pushState = function (...args) {
      const ret = origPush.apply(this, args);
      notify();
      return ret;
    };
    window.history.replaceState = function (...args) {
      const ret = origReplace.apply(this, args);
      notify();
      return ret;
    };

    return () => {
      window.removeEventListener('popstate', handle);
      window.removeEventListener('locationchange', handle);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, [
    props.title,
    props.baseTitle,
    props.baseMetaTitle,
    props.baseMetaDescription,
    props.topicLabelMap,
    props.contentTypeLabelMap,
    props.contentTypePluralLabelMap,
    props.locale,
    props.templates,
    props.contentTypeGenderMap,
    props.brandName,
    props.introTextSSR,
  ]);

  const title = dynamicTitle;
  const className = props.className;

  const emitGAEvent = searchTerm => {
    if (window !== undefined && window.dataLayer !== undefined) {
      window.dataLayer.push({
        event: 'view_search_results',
        eventModel: { search_term: searchTerm },
      });
    }
  };

  function setQuery(newQuery) {
    updateSearchValue(newQuery);
    refine(newQuery);
  }

  return (
    <div className={className} role="banner">
      <div className="title-wrap">
        <h1>{sentenceCase(title, props.locale)}</h1>
      </div>
      <form
        noValidate=""
        className="ais-SearchBox-form"
        action=""
        role="search"
        onSubmit={e => {
          e.preventDefault();
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-label="Search icon"
        >
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
        <input
          ref={inputRef}
          className="ais-SearchBox-input"
          type="search"
          data-testid="searchbox-input"
          placeholder={Translate('Start your search...', true)}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          required=""
          maxLength="512"
          aria-label="Search"
          onChange={event => setQuery(event.target.value)}
          value={query}
        />
        <button
          type="reset"
          title={Translate('Clear the search query', true)}
          className="ais-SearchBox-reset"
          onClick={() => {
            updateSearchValue('');
            refine('');
          }}
        >
          <svg
            className="ais-SearchBox-resetIcon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            width="10"
            height="10"
            aria-label="Clear search box icon"
          >
            <path d="M8.114 10L.944 2.83 0 1.885 1.886 0l.943.943L10 8.113l7.17-7.17.944-.943L20 1.886l-.943.943-7.17 7.17 7.17 7.17.943.944L18.114 20l-.943-.943-7.17-7.17-7.17 7.17-.944.943L0 18.114l.943-.943L8.113 10z"></path>
          </svg>
        </button>
      </form>
    </div>
  );
};

const StyledSearchBox = styled(SearchBox)`
  padding: 8.5% 10%;
  margin-top: ${vars.primaryNavHeight};
  background-color: #c4c4c4;
  background-image: url(${({ backgroundImageSrc }) =>
    backgroundImageSrc || ''});
  background-size: cover;
  .title-wrap {
    text-align: center;
  }

  h1 {
    font-weight: ${vars.fontWeightExtraBold};
    font-size: 3.8rem;
    color: #ffffff;
    line-height: 110%;
    padding: 8px;
    text-align: center;
    display: inline-block;
    margin: 0 auto 22px auto;
  }
  form {
    display: flex;
    align-items: center;
    border-radius: 8px;
    width: 100%;
    display: flex;
    padding: 0;
    margin: 0 auto;
    background-color: ${vars.white};
    max-width: ${vars.contentContained};
  }
  form svg {
    margin-left: 2.6rem;
    color: ${vars.coreDarkGrey};
  }
  .ais-SearchBox-form {
    .ais-SearchBox-input {
      padding: ${vars.spacingS} ${vars.spacingXl} ${vars.spacingS} 2.6rem;
      line-height: 4.8rem;
      font-weight: ${vars.fontWeightRegular};
      font-size: 2rem;
      color: ${vars.coreDarkGrey};
      border: none;
      width: 90%;
      outline: none;
      margin: 0;
    }
    .ais-SearchBox-input::placeholder {
      color: ${vars.coreDarkGrey};
      opacity: 1;
    }
    .ais-SearchBox-input:-ms-input-placeholder {
      color: ${vars.coreDarkGrey};
    }
    .ais-SearchBox-input::-ms-input-placeholder {
      color: ${vars.coreDarkGrey};
    }
    input[type='search']::-webkit-search-cancel-button {
      -webkit-appearance: none;
    }
    .ais-SearchBox-submit,
    .ais-SearchBox-reset {
      width: 5%;
      line-height: 4.8rem;
      width: 5%;
      border: none;
      background-color: transparent;
      outline: none;
      cursor: pointer;
    }
    .ais-SearchBox-submitIcon,
    .ais-SearchBox-resetIcon {
      width: 2rem;
      height: 2rem;
    }
    .ais-SearchBox-reset {
      line-height: normal;
      opacity: 0.5;
      margin-top: 3px;
      margin-right: 16px;
      @media screen and (min-width: ${vars.breakPointSmallTablet}) {
        margin-right: 8px;
      }
    }
    .ais-SearchBox-reset div {
      font-weight: ${vars.fontWeightRegular};
    }
    .ais-SearchBox-resetIcon {
      margin-left: 0;
      width: 1.5rem;
      height: 1.5rem;
    }
    .ais-SearchBox-resetIcon:after {
    }
  }
`;

export default StyledSearchBox;
